import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "~constants/pagination.ts";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { defaultVenuesExportFieldNames, venuesExportFields } from "~pages/Venues/VenueListPage/constants/exportFields.ts";
import { useDeleteVenue } from "~pages/Venues/VenueListPage/hooks/useDeleteVenue.ts";
import { useExportVenues } from "~pages/Venues/VenueListPage/hooks/useExportVenues.ts";
import { useFetchVenues } from "~pages/Venues/VenueListPage/hooks/useFetchVenues.ts";
import { useGetVenueTableColumns } from "~pages/Venues/VenueListPage/hooks/useGetVenueTableColumns.tsx";
import { useGetVenueTableFilters } from "~pages/Venues/VenueListPage/hooks/useGetVenueTableFilters.tsx";
import { useImportVenues } from "~pages/Venues/VenueListPage/hooks/useImportVenues.ts";

const VenueListPage = () => {
  const paginationConfig = useHandlePagination(20, 1, DEFAULT_PAGE_SIZE_OPTIONS);
  const { sport, customerId, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const location = useLocation();
  const { mutateAsync: startImporting } = useImportVenues();
  const { mutateAsync: startExport, isPending: isExportPending } = useExportVenues();
  const navigate = useNavigate();
  const handleAdd = () => navigate(`/${customerId}/admin/${sport}/${organizationId}/venues/manage/`);
  const [searchText, setSearchText] = useState(location.state?.search || "");

  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.venues.listPage;
  const dataTableImportConfiguration = uiConfiguration.pages.venues.listPage.importConfig;
  const [sortBy, setSortBy] = useState<string | null>(null);
  const filterConfig = useGetVenueTableFilters();

  const { data, refetch, isPending } = useFetchVenues({
    enabled: sortBy !== null,
    customParams: {
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      sortBy: sortBy || "",
      status: filterConfig.currentFilter.status?.value,
      countryCode: filterConfig.currentFilter.countryCode?.value,
      nameLocalContains: searchText,
    },
  });

  const { mutateAsync: deleteFn, isPending: isDeletePending } = useDeleteVenue();

  const handleDelete = useCallback(
    async (venueId: string) => {
      return await deleteFn(venueId);
    },
    [deleteFn],
  );

  const columns = useGetVenueTableColumns();

  const dataSource = data?.data || [];

  const getNavigateUrl = (venueId: string) => `/${customerId}/admin/${sport}/${organizationId}/venues/manage/${venueId}`;

  return (
    <DataTable
      handleRefresh={() => {
        refetch();
      }}
      title={formatMessage({ id: "venuesListPage.title" })}
      columns={columns}
      rowActions={{
        base: {
          delete: {
            onClick: (row) => handleDelete(row.venueId),
            loading: isDeletePending,
          },
          manage: {
            onClick: (row) => navigate(getNavigateUrl(row.venueId)),
          },
        },
      }}
      rowKey={"venueId"}
      rowNameKey={"nameLocal"}
      dataSource={dataSource}
      columnChooserLocalStorageKey={`${sport}.tables.settings.columns.venues`}
      isDataLoading={isPending}
      handleAdd={handleAdd}
      addButtonIntlKey={"venue"}
      filtersConfig={filterConfig}
      searchConfig={{
        handleSearch: setSearchText,
        searchText: searchText,
      }}
      columnSortConfig={{
        sorterLocalStorageKey: `${sport}.tables.settings.sort.venues`,
        defaultSorter: {
          columnKey: "nameLocal",
          order: "descend",
        },
        onSortChange: setSortBy,
      }}
      exportConfig={{
        handleExport: startExport,
        loading: isExportPending,
        defaultFields: defaultVenuesExportFieldNames,
        groupedFields: venuesExportFields,
        localStorageKey: "export.venues",
      }}
      importConfig={{
        ...dataTableImportConfiguration,
        handleImport: startImporting,
      }}
      permissionNames={[PermissionNames.VENUE]}
      dataTableUiConfiguration={dataTableUiConfiguration}
      paginationConfig={{ ...paginationConfig, hasNext: data?.hasNextLink }}
    />
  );
};

export default VenueListPage;
